import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";

export default function SetPassword() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/set-password", { new_password: password });
      setUser({ ...user, force_password_change: false });
      navigate(user?.role === "admin" ? "/admin" : "/dealer", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6" data-testid="set-password-page">
      <div className="w-full max-w-sm page-enter">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">
          First sign-in
        </p>
        <h1 className="mt-4 font-display text-4xl font-light tracking-tight text-charcoal">
          Set your password
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-warmgrey">
          Your account was created with a temporary password. Choose a new password to continue.
        </p>

        <form onSubmit={handleSubmit} className="mt-10 space-y-6" data-testid="set-password-form">
          {error && (
            <Alert variant="destructive" data-testid="set-password-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">New password</Label>
            <PasswordInput
              required
              minLength={8}
              data-testid="new-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11 border-border bg-white focus-visible:ring-terracotta"
            />
            <p className="text-xs text-muted-foreground">Minimum 8 characters.</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Confirm password</Label>
            <PasswordInput
              required
              minLength={8}
              data-testid="confirm-password-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="h-11 border-border bg-white focus-visible:ring-terracotta"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="set-password-submit"
            className="h-11 w-full bg-terracotta text-white transition-all duration-200 hover:-translate-y-[1px] hover:bg-terracotta-hover"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set password & continue"}
          </Button>
        </form>
      </div>
    </div>
  );
}
